import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Results } from '@local/client-contracts';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { KeyboardService } from '@shared/services/keyboard.service';
import { getModifiers, KeyName } from '@local/ts-infra';
import { ComponentFocused } from 'src/app/bar/models/component-focused.model';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { TelemetryTrigger } from 'src/app/bar/views/results';
import { InvokeCommand } from 'src/app/bar/views/results/models/invoke-command.model';

@UntilDestroy()
@Component({
  selector: 'mail-preview-attachment',
  templateUrl: './mail-preview-attachment.component.html',
  styleUrls: ['./mail-preview-attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailPreviewAttachmentComponent implements OnDestroy {
  //keyboard
  private readonly COMP_INDEX = 2;
  private keyHandlerId: string;
  markedIndex: number;

  private _attachments: Results.Attachment[] = [];
  openContextMenu: { x: number; y: number };

  @Input() set attachments(value: Results.Attachment[]) {
    this._attachments = value;
    this.markedIndex = -1;
    this.cdr.markForCheck();
  }
  @Input() dragMetadata: DragMetadata;

  @Input() set componentFocused(val: ComponentFocused) {
    if (val?.compIndex === this.COMP_INDEX) {
      if (this.keyHandlerId) return;
      this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
        this.handleKeys(keys, event);
      });
      this.markedIndex = val?.direction === 'first' ? 0 : this.attachments.length - 1;
      return;
    }
    this.unregisterKeyHandler();
  }

  @Output() componentFocusedExit: EventEmitter<string> = new EventEmitter<string>();

  get attachments(): Results.Attachment[] {
    return this._attachments;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private keyboardService: KeyboardService,
    private resultCommandService: ResultCommandService
  ) {}

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
  }

  unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.keyHandlerId = null;
    this.markedIndex = -1;
  }

  async handleKeys(keys: Array<KeyName>, event): Promise<void> {
    const modifiers = getModifiers(keys);
    switch (keys[0]) {
      case 'tab': {
        event.stopPropagation();
        event.preventDefault();
        if (modifiers.length && modifiers[0] === 'shift') {
          if (this.markedIndex === 0) {
            this.componentFocusedExit.emit('prev');
          }
          this.markedIndex--;
          break;
        }
        if (this.markedIndex === this.attachments.length - 1) {
          this.componentFocusedExit.emit('next');
        } else this.markedIndex++;
        break;
      }
    }
    this.cdr.markForCheck();
  }

  onInvoke(invokeData: InvokeCommand, trigger: TelemetryTrigger) {
    this.resultCommandService.executeCommand(invokeData?.command, invokeData?.context, 'result', { target: trigger });
  }
}
